import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useFormikContext } from "formik";
import groupBy from "lodash/groupBy";
import orderBy from "lodash/orderBy";
import Card from "@mui/material/Card";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";

import Input from "../../../components/form/Input";
import { usePermissions } from "../../../contexts/permissions";
import CartItemsReorder from "./CartItemsReorder";
import CartItemsView from "./CartItemsView";
import useInventoryItemsFilter from "../../../hooks/useInventoryItemsFilter";
import VirtualizedCartLocationTable from "./VirtualizedCartLocationTable";
import CartItemMobileCard from "./CartItemMobileCard";

export function useLocations(inventoryItems) {
  const { values } = useFormikContext();
  const [list, setList] = useState([]);

  useEffect(() => {
    const locationOrder = values.locationOrder || {};
    const newList = Object.entries(
      groupBy(
        orderBy(Object.values(inventoryItems), "position").map((v, index) => ({ ...v, index })),
        "location"
      )
    );
    const orderedList = orderBy(newList, item => locationOrder[item[0].split(",")[0]]);
    setList(orderedList);
  }, [values, inventoryItems]);
  return [list, setList];
}

export default function CartItems({
  items,
  display,
  submitCounter,
  removeInventoryItem,
  reFetch,
  locationNotesDisabled,
  actions,
  disabled = false,
  centerAligned = false,
}) {
  const { values, setFieldValue } = useFormikContext();
  const { user } = usePermissions();
  const wizardMode = user.company?.settings?.wizard_mode;
  const isMobile = useMediaQuery(theme => theme.breakpoints.down("sm"));
  const [view, setView] = useState("edit");
  const { searchTerm, filteredItems, handleSearchChange } = useInventoryItemsFilter(items);

  const orderedItems = useMemo(() => {
    return wizardMode
      ? filteredItems.map((v, index) => ({ ...v, index }))
      : filteredItems.map((v, index) => ({ ...v, location: values.location, index }));
  }, [wizardMode, filteredItems, values.location]);

  const [orderedList, setOrderedList] = useLocations(orderedItems);
  const hasSkuNumbers = items.some(item => item.asset?.skuNumber);
  const hasModels = items.some(item => item.asset?.model);
  const hasBarcode = items.some(item => item.asset?.barcode);
  const hasDimensions = items.some(item => {
    const dimensions = item.asset?.dimensions;
    if (!dimensions || dimensions.length === 0) return false;
    return dimensions.some(dim => dim.depth > 0 || dim.width > 0 || dim.height > 0);
  });

  const tableDisplayColumns = {
    ...display,
    ii_image: true,
    ii_item_id: true,
    ii_description: true,
    ii_qtyRequested: true,
    ii_sku_number: hasSkuNumbers,
    ii_model: hasModels,
    ii_barcode: hasBarcode,
    ii_dimensions: hasDimensions,

    // Commented out because they are not used in the cart items view
    // ii_notes: true,
    // ii_building: true,
    // ii_floor: true,
    // ii_room: true,
    // ii_location: true,
    // ii_po_number: true,
    // ii_idsr_number: true,
    // ii_eoc_number: true,
    // ii_deliveryDate: true,
    // ii_deliveryTime: true,
  };

  const handleRemoveInventoryItem = useCallback(
    id => {
      removeInventoryItem(id);
      setFieldValue(
        "inventoryItems",
        items.filter(item => item.id !== id)
      );
    },
    [removeInventoryItem, setFieldValue, items]
  );

  return (
    <Box>
      <Box
        pb={2}
        pt={0.5}
        display="flex"
        justifyContent="space-between"
        flexDirection={{
          xs: "column",
          sm: "row",
        }}
        gap={1}
      >
        <TextField
          size="small"
          placeholder="Search"
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ width: { xs: `100%`, sm: 320 } }}
        />
        {actions}
      </Box>
      <Card>
        {wizardMode && (
          <Box mb={2}>
            <CartItemsView value={view} onChange={setView} />
          </Box>
        )}
        {view === "edit" ? (
          <Box minWidth={isMobile ? undefined : 700}>
            {orderedList.length === 0 && searchTerm ? (
              <Box p={2} textAlign="center">
                <Typography variant="body2" color="textSecondary">
                  No items found matching "{searchTerm}"
                </Typography>
              </Box>
            ) : (
              orderedList.map(([value, list], index) => (
                <Box
                  key={value}
                  mt={index === 0 ? 0 : 3}
                  borderRadius={3}
                  p={2}
                  border="1px solid #3f51b5"
                >
                  {wizardMode && (
                    <>
                      <Input
                        type="text"
                        name={`locationNotes[${value.split(",")[0]}]`}
                        label={`${value.split(",")[0]} Scope`}
                        disabled={locationNotesDisabled}
                      />
                      <Box mt={2} />
                    </>
                  )}
                  <Paper>
                    {isMobile ? (
                      <Box>
                        {list.map(item => (
                          <CartItemMobileCard
                            key={`${item.id}-${item.asset?.id}`}
                            inventoryItem={item}
                            asset={item.asset}
                            display={tableDisplayColumns}
                            submitCounter={submitCounter}
                            reFetch={reFetch}
                            removeInventoryItem={handleRemoveInventoryItem}
                            setFieldValue={setFieldValue}
                            inventoryItemsCount={items.length}
                            disabled={disabled}
                          />
                        ))}
                      </Box>
                    ) : (
                      <VirtualizedCartLocationTable
                        list={list}
                        display={tableDisplayColumns}
                        submitCounter={submitCounter}
                        reFetch={reFetch}
                        removeInventoryItem={handleRemoveInventoryItem}
                        setFieldValue={setFieldValue}
                        inventoryItemsCount={items.length}
                        disabled={disabled}
                        centerAligned={centerAligned}
                      />
                    )}
                  </Paper>
                </Box>
              ))
            )}
          </Box>
        ) : (
          <CartItemsReorder
            items={orderedList}
            setOrderedList={setOrderedList}
            display={tableDisplayColumns}
            reFetch={reFetch}
          />
        )}
      </Card>
    </Box>
  );
}
